import {useMemo, useState} from "react";
import {Button, Stack, Typography} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {appStyles} from "../../../../AppStyles.ts";
import {StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../../AppStyledComponents.tsx";
import type {AnalystInsight} from "../../../../sdo/AnalystInsight.ts";
import InsightCard from "../../intel/panels/insights/InsightCard.tsx";

let nextId = 100;

function createEmptyInsight(): AnalystInsight {
    return {
        uid: String(nextId++),
        title: "",
        content: "",
        tags: [],
        createdBy: "CurrentUser",
        createdAt: Date.now(),
        modifiedAt: 0,
        version: 1,
    };
}

// TODO: Replace with API call to load insights from AResourceProfileManager.readInsights()
function buildTestData(): AnalystInsight[] {
    return [
        {
            uid: "1",
            title: "Source document quality concerns",
            content: "The OCR pass on the scanned pages produced several questionable extractions in the second half of the document. Recommend a manual re-review for pages 14-22 before relying on derived intel from this resource.",
            tags: ["ocr", "data-quality", "review-needed"],
            createdBy: "m.chen",
            createdAt: new Date("2026-03-20T11:15:00").getTime(),
            modifiedAt: 0,
            version: 1,
        },
        {
            uid: "2",
            title: "Cross-reference candidates identified",
            content: "Three named entities in this document overlap with active intel profiles. The strongest match is on the second mention in the introduction section. Promoting these to the cross-reference list for analyst review.",
            tags: ["cross-ref", "entity-match"],
            createdBy: "j.doe",
            createdAt: new Date("2026-03-25T15:30:00").getTime(),
            modifiedAt: new Date("2026-03-26T09:00:00").getTime(),
            version: 2,
        },
        {
            uid: "3",
            title: "Provenance verified",
            content: "Source chain confirmed via partner feed metadata.",
            tags: [],
            createdBy: "a.patel",
            createdAt: new Date("2026-04-02T08:20:00").getTime(),
            modifiedAt: 0,
            version: 1,
        },
        {
            uid: "4",
            title: "Structural notes for downstream consumers",
            content: "This resource is part of a series that the partner releases on a quarterly cadence. Future updates should be tagged consistently so the timeline view picks them up. The first three editions follow the same section layout, so the metadata extractor configuration should remain stable across them. The fourth edition introduces a new appendix that may need a separate parser pass.",
            tags: ["series", "parser-config", "quarterly"],
            createdBy: "j.doe",
            createdAt: new Date("2026-04-05T13:45:00").getTime(),
            modifiedAt: 0,
            version: 1,
        },
    ];
}

function UXResourceAnalystPanel() {
    const [insights, setInsights] = useState<AnalystInsight[]>(buildTestData);
    const [filterText, setFilterText] = useState("");
    const [newInsightUid, setNewInsightUid] = useState<string | null>(null);

    const filtered = useMemo(() => {
        if (!filterText.trim()) return insights;
        const q = filterText.toLowerCase();
        return insights.filter((i) => JSON.stringify(i).toLowerCase().includes(q));
    }, [insights, filterText]);

    // TODO: Replace with API call to AResourceProfileManager.deleteInsight()
    const handleDelete = (uid: string) => {
        setInsights((prev) => prev.filter((i) => i.uid !== uid));
        if (newInsightUid === uid) setNewInsightUid(null);
    };

    // TODO: Replace with API call to AResourceProfileManager.saveInsight()
    const handleUpdate = (updated: AnalystInsight) => {
        setInsights((prev) => prev.map((i) => i.uid === updated.uid ? updated : i));
    };

    const handleCreate = () => {
        const newInsight = createEmptyInsight();
        setInsights((prev) => [newInsight, ...prev]);
        setNewInsightUid(newInsight.uid);
    };

    return (
        <Stack sx={appStyles.defaultPanelContainer} direction="column" padding={1} width="100%">
            {/* Title */}
            <Stack direction="row" padding={1} justifyContent="center">
                <Typography sx={appStyles.panelSectionTitle}>Analyst Insights</Typography>
            </Stack>

            {/* Filter bar */}
            <Stack direction="row" spacing={1} padding={1} alignItems="center" justifyContent="center" sx={{position: "relative"}}>
                <StyledSearch>
                    <StyledSearchIconWrapper>
                        <SearchIcon sx={{fontSize: 16, color: "#7093a0"}}/>
                    </StyledSearchIconWrapper>
                    <StyledInputBase
                        placeholder="Filter insights..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </StyledSearch>

                <Typography sx={appStyles.panelFilterCount}>
                    Showing {filtered.length} of {insights.length}
                </Typography>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={handleCreate}
                    sx={{...appStyles.panelActionButton, position: "absolute", right: 8}}
                >
                    Create Insight
                </Button>
            </Stack>

            {/* Insight cards list */}
            <Stack direction="column" spacing={1} sx={{...appStyles.defaultPanelCardsWrapper, padding: "8px", alignItems: "stretch"}}>
                {filtered.map((insight) => (
                    <InsightCard
                        key={insight.uid}
                        insight={insight}
                        onDelete={handleDelete}
                        onUpdate={handleUpdate}
                        startInEditMode={insight.uid === newInsightUid}
                    />
                ))}
                {filtered.length === 0 && (
                    <Typography sx={{
                        color: "rgba(242, 245, 250, 0.4)",
                        fontSize: "0.80rem",
                        textAlign: "center",
                        padding: "24px",
                    }}>
                        No analyst insights match the current filter.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default UXResourceAnalystPanel;
